import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import Button from "../../../components/common/Button/Button";
import Modal from "../../../components/common/Modal/Modal";
import { FiDownload } from "react-icons/fi";
import { MdOutlineFileDownload } from "react-icons/md";
import PieChartGraph from "../../../components/common/Charts/PieChartGraph";
import { getApiInstance } from "../../../utils/api";
import { useZone } from "../../../context/ZoneContext";
import { useMoneda } from "../../../context/MonedaContext";
import { FilterBar } from "../../../components/filtersNew/filter-barNew";
import ExcluirRaeeZeroWasteToggle from "../../../components/common/Inputs/ExcluirRaeeZeroWasteToggle/ExcluirRaeeZeroWasteToggle";
import { createFilterState } from "../../../lib/reportNew/filter-stateNew";
import {
    useExcelExport,
    buildInformeExportFilters,
    codsFilaDe,
} from "../useExcelExport";
import {
    INDICADORES,
    QUICK_FILTERS_INDICADOR,
    mapaPaises,
    mapaTiposObra,
    normalizarFilas,
    filasVisibles,
} from "../indicadores/definiciones";
import "../cantidadesGeneradas/CantidadesGeneradas.scss";
import "./CostesGestion.scss";

const INDICADOR = INDICADORES.costesGestion;

const COLOR_CON_COSTES = "#10b981";
const COLOR_SIN_COSTES = "#9ca3af";

export default function CostesGestion() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [datos, setDatos] = useState([]);

    const { selectedZone } = useZone();
    const { moneda } = useMoneda();

    const { isModalExcelOpen, openModal, closeModal, handleExportToExcel, handleExportInformeCompleto, exportando } =
        useExcelExport({
            onError: setError,
            resumenUrl: "api/Informe/CreateReportExcelCostesGestionResumen",
        });

    const { filterFields } = INDICADOR;

    const [filterState, setFilterState] = useState(() =>
        createFilterState(QUICK_FILTERS_INDICADOR)
    );

    const [paisesArr, setPaisesArr] = useState([]);
    const [tiposObraArr, setTiposObraArr] = useState([]);

    /* Se vuelve a pedir al cambiar de moneda: la conversión la hace el backend. */
    useEffect(() => {
        if (!moneda) return;

        const fetchDatos = async () => {
            setLoading(true);
            try {
                const api = getApiInstance();
                const response = await api.get(INDICADOR.datosUrl(moneda));
                setDatos(INDICADOR.filasDe(response.data));
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchDatos();
    }, [moneda]);

    useEffect(() => {
        const api = getApiInstance();

        const fetchPaises = async () => {
            try {
                const response = await api.get(`/api/Paises/GetPaises`);
                setPaisesArr(
                    Array.isArray(response.data) ? response.data : []
                );
            } catch (err) {
                console.error(err);
            }
        };
        fetchPaises();

        const fetchTiposObra = async () => {
            try {
                const response = await api.get(
                    `/api/ObrasTipo/GetAllObrasTipo`
                );
                setTiposObraArr(
                    Array.isArray(response.data) ? response.data : []
                );
            } catch (err) {
                console.error(err);
            }
        };
        fetchTiposObra();
    }, []);

    const mapPaises = useMemo(() => mapaPaises(paisesArr), [paisesArr]);
    const mapTiposObra = useMemo(
        () => mapaTiposObra(tiposObraArr),
        [tiposObraArr]
    );
    const reversePaises = useMemo(
        () =>
            Object.fromEntries(
                paisesArr.map((p) => [String(p.pais), String(p.codPais)])
            ),
        [paisesArr]
    );
    const reverseTiposObra = useMemo(
        () =>
            Object.fromEntries(
                tiposObraArr.map((t) => [
                    String(t.descripcion),
                    String(t.codTipoObra),
                ])
            ),
        [tiposObraArr]
    );

    const datosNormalizados = useMemo(
        () => normalizarFilas(datos, mapPaises, mapTiposObra),
        [datos, mapPaises, mapTiposObra]
    );

    const [excluirRaeeZeroWaste, setExcluirRaeeZeroWaste] = useState(false);

    const datosFiltrados = useMemo(
        () =>
            filasVisibles(
                INDICADOR,
                datosNormalizados,
                filterState,
                excluirRaeeZeroWaste
            ),
        [datosNormalizados, filterState, excluirRaeeZeroWaste]
    );

    const exportFilters = useMemo(() => {
        const quick = filterState.quick || {};
        const anios = Array.isArray(quick.anio) ? quick.anio : [];
        const paises = Array.isArray(quick.pais) ? quick.pais : [];
        const tipos = Array.isArray(quick.tipoObra) ? quick.tipoObra : [];
        return {
            ...buildInformeExportFilters({
                selectedAnio: anios.length ? anios[0] : "all",
                selectedPais: paises.length
                    ? reversePaises[paises[0]] ?? paises[0]
                    : "all",
                selectedTipoObra: tipos.map((t) => reverseTiposObra[t] ?? t),
                selectedZonas: quick.codZona,
            }),
            /* Lo que manda de verdad: las filas que se están viendo. Los filtros
               de arriba se quedan por compatibilidad. */
            codsFila: codsFilaDe(datosFiltrados),
        };
    }, [filterState, reversePaises, reverseTiposObra, datosFiltrados]);

    const { totalObras, obrasConCostes, pctCostes, hasData } = useMemo(() => {
        const obrasTotales = datosFiltrados.length;
        const conCostes = datosFiltrados.filter(
            (obra) => Number(obra.conCostes) === 1
        ).length;

        return {
            totalObras: obrasTotales,
            obrasConCostes: conCostes,
            pctCostes: obrasTotales > 0 ? (conCostes / obrasTotales) * 100 : 0,
            hasData: obrasTotales > 0,
        };
    }, [datosFiltrados]);

    const datosTarta = useMemo(() => {
        if (!hasData) {
            return [];
        }
        const tarta = [];
        if (obrasConCostes > 0 && pctCostes > 0) {
            tarta.push({
                name: "Con costes de gestión",
                value: pctCostes,
            });
        }
        const pctSinCostes = totalObras > 0 ? 100 - pctCostes : 0;
        if (totalObras - obrasConCostes > 0 && pctSinCostes > 0) {
            tarta.push({ name: "Sin costes de gestión", value: pctSinCostes });
        }
        return tarta;
    }, [obrasConCostes, totalObras, pctCostes, hasData]);

    const renderFilters = () => (
        <div className="filters-section-common">
            <FilterBar
                rows={datosNormalizados}
                state={filterState}
                onChange={setFilterState}
                resultCount={datosFiltrados.length}
                totalCount={datosNormalizados.length}
                fields={filterFields}
                excludeFields={INDICADOR.excludeFields}
                headerExtra={
                    <ExcluirRaeeZeroWasteToggle
                        activo={excluirRaeeZeroWaste}
                        onChange={setExcluirRaeeZeroWaste}
                    />
                }
            />
        </div>
    );

    return (
        <div className="costes-gestion-page">
            <PageLayout
                title="Informe de Costes de Gestión"
                subtitle="Número de obras con costes de gestión frente al total"
                actions={[
                    <Button
                        key="export"
                        componentType="button"
                        variant="filled"
                        startIcon={<FiDownload />}
                        onClick={openModal}
                    >
                        Exportar a excel
                    </Button>,
                ]}
            >
                <div className="tab-content-with-filters">
                    {renderFilters()}

                    <div className="cantidades-generadas-content">
                        <div className="graficas-card">
                            <div className="charts-section">
                                <div className="chart-container pie-chart-container">
                                    <div className="chart-header valorizacion-header">
                                        <div className="valorizacion-header__left">
                                            <h3>Costes de Gestión</h3>
                                            <p className="costes-formula">
                                                % Obras con costes de gestión =
                                                (nº obras con costes / nº obras
                                                totales) * 100
                                            </p>
                                        </div>
                                        <div className="valorizacion-header__percentage">
                                            {pctCostes.toFixed(1)}%
                                        </div>
                                    </div>

                                    <div className="chart-wrapper">
                                        {loading ? (
                                            <div
                                                className="informe-chart-skeleton"
                                                aria-hidden="true"
                                            >
                                                <div className="informe-chart-skeleton__donut" />
                                                <div className="informe-chart-skeleton__lines">
                                                    <div className="informe-chart-skeleton__line" />
                                                    <div className="informe-chart-skeleton__line informe-chart-skeleton__line--medium" />
                                                    <div className="informe-chart-skeleton__line informe-chart-skeleton__line--short" />
                                                </div>
                                            </div>
                                        ) : (
                                            <PieChartGraph
                                                data={datosTarta}
                                                colors={[
                                                    COLOR_CON_COSTES,
                                                    COLOR_SIN_COSTES,
                                                ]}
                                            />
                                        )}
                                    </div>

                                    <div className="segregacion-legend">
                                        <div className="segregacion-legend__item">
                                            <span
                                                className="segregacion-legend__dot"
                                                style={{
                                                    background: COLOR_CON_COSTES,
                                                }}
                                            />
                                            <span className="segregacion-legend__label">
                                                Verde: Con costes de gestión
                                            </span>
                                            <span className="segregacion-legend__value">
                                                {hasData ? obrasConCostes : "—"}
                                            </span>
                                        </div>
                                        <div className="segregacion-legend__item">
                                            <span
                                                className="segregacion-legend__dot"
                                                style={{
                                                    background: COLOR_SIN_COSTES,
                                                }}
                                            />
                                            <span className="segregacion-legend__label">
                                                Gris: Sin costes de gestión
                                            </span>
                                            <span className="segregacion-legend__value">
                                                {hasData
                                                    ? totalObras - obrasConCostes
                                                    : "—"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                <Modal
                    isOpen={isModalExcelOpen}
                    onClose={closeModal}
                    title="Exportar informe a Excel"
                    actions={[
                        <Button
                            key="export-resumen"
                            className="export-excel__btn export-excel__btn--primary"
                            onClick={() =>
                                handleExportToExcel("resumen", exportFilters)
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "resumen"
                                ? "Generando..."
                                : "Informe Costes de Gestión"}
                        </Button>,
                        <Button
                            key="export-completo"
                            className="export-excel__btn export-excel__btn--secondary"
                            onClick={() =>
                                handleExportInformeCompleto({ filterState, excluirRaeeZeroWaste })
                            }
                            startIcon={<MdOutlineFileDownload />}
                            disabled={exportando}
                        >
                            {exportando === "completo"
                                ? "Generando..."
                                : "Informe Completo"}
                        </Button>,
                    ]}
                >
                    <div className="export-excel__actions"></div>
                </Modal>
            </PageLayout>
        </div>
    );
}
